import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Typography } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { TransferenciaResumoResponse } from 'common/genericTypes';
import CompactUpload from 'components/compactUpload';
import { Button, SmSpace } from 'm2m.components';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AprovacaoLoteResponse,
  POSTAprovacaoTransferenciaEmLote,
  POSTTransferenciaLoteComprovantes,
} from 'requests/operacao';
import { addAlert } from 'store/modules/snackBar';
import { addCpfCnpjMask } from 'utils/mask';

interface AprovacaoEmLoteTransferenciasModalProps {
  open: boolean;
  operacoes: TransferenciaResumoResponse[];
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'form' | 'feedback';

interface DocumentoUploadResponse {
  status: 'SUCESSO' | 'PARCIAL' | 'FALHA';
  mensagem: string;
  resumo: {
    totalProcessados: number;
    totalSucesso: number;
    totalFalha: number;
  };
  itens: Array<{
    operacaoId: string;
    sucesso: boolean;
    documento: any;
    erro: { httpStatus: number; mensagem: string } | null;
  }>;
}

const statusConfig = {
  SUCESSO: {
    icon: <CheckCircleOutlineIcon style={{ fontSize: 40, color: '#4caf50' }} />,
    color: '#4caf50',
    titulo: 'Aprovação em lote processada',
  },
  PARCIAL: {
    icon: <WarningIcon style={{ fontSize: 40, color: '#ff9800' }} />,
    color: '#ff9800',
    titulo: 'Aprovação em lote processada parcialmente',
  },
  FALHA: {
    icon: <ErrorOutlineIcon style={{ fontSize: 40, color: '#f44336' }} />,
    color: '#f44336',
    titulo: 'Aprovação em lote falhou',
  },
};

export const AprovacaoEmLoteTransferenciasModal: React.FC<AprovacaoEmLoteTransferenciasModalProps> = ({
  open,
  operacoes,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [comprovantesPorOperacao, setComprovantesPorOperacao] = useState<Map<string, File[]>>(new Map());
  const [step, setStep] = useState<ModalStep>('form');
  const [feedbackData, setFeedbackData] = useState<AprovacaoLoteResponse | null>(null);
  const [documentosFeedback, setDocumentosFeedback] = useState<DocumentoUploadResponse | undefined>(undefined);

  const handleArquivosChange = (operacaoId: string, novosArquivos: File[]) => {
    setComprovantesPorOperacao((prev) => {
      const updated = new Map(prev);
      const existentes = updated.get(operacaoId) ?? [];
      updated.set(operacaoId, [...existentes, ...novosArquivos]);
      return updated;
    });
  };

  const handleArquivoRemove = (operacaoId: string, index: number) => {
    setComprovantesPorOperacao((prev) => {
      const updated = new Map(prev);
      const existentes = updated.get(operacaoId) ?? [];
      updated.set(
        operacaoId,
        existentes.filter((_, i) => i !== index)
      );
      return updated;
    });
  };

  const handleAprovar = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      let resultado: AprovacaoLoteResponse;

      try {
        resultado = await POSTAprovacaoTransferenciaEmLote(
          dispatch,
          operacoes.map((op) => op.id),
          true
        );
      } catch (error: any) {
        const responseData = error?.response?.data;
        if (responseData?.status && responseData?.resumo && responseData?.itens) {
          resultado = responseData as AprovacaoLoteResponse;
        } else {
          const msg =
            responseData?.message ?? responseData?.errors?.join(', ') ?? 'Erro ao aprovar transferências em lote.';
          dispatch(addAlert({ severity: 'error', message: msg }));
          setSubmitting(false);
          return;
        }
      }

      const idsAprovados = resultado.itens.filter((i) => i.sucesso).map((i) => i.operacaoId);

      const itensDocumento = Array.from(comprovantesPorOperacao.entries())
        .filter(([operacaoId]) => idsAprovados.includes(operacaoId))
        .flatMap(([operacaoId, arquivos]) => arquivos.map((arquivo) => ({ operacaoId, arquivo })));

      let documentoResult: DocumentoUploadResponse | undefined;

      if (itensDocumento.length > 0) {
        try {
          documentoResult = (await POSTTransferenciaLoteComprovantes(
            dispatch,
            itensDocumento,
            true
          )) as DocumentoUploadResponse;
        } catch (error: any) {
          const responseData = error?.response?.data;
          if (responseData?.status && responseData?.resumo && responseData?.itens) {
            documentoResult = responseData as DocumentoUploadResponse;
          } else {
            documentoResult = {
              status: 'FALHA',
              mensagem: 'Erro ao enviar documentos',
              resumo: {
                totalProcessados: itensDocumento.length,
                totalSucesso: 0,
                totalFalha: itensDocumento.length,
              },
              itens: itensDocumento.map((i) => ({
                operacaoId: i.operacaoId,
                sucesso: false,
                documento: null,
                erro: { httpStatus: 500, mensagem: 'Erro de comunicação ao enviar documento' },
              })),
            };
          }
        }
      }

      setFeedbackData(resultado);
      setDocumentosFeedback(documentoResult);
      setStep('feedback');
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setComprovantesPorOperacao(new Map());
    setStep('form');
    setFeedbackData(null);
    setDocumentosFeedback(undefined);
    onClose();
  };

  const descricaoQuantidade =
    operacoes.length === 1
      ? '1 transferência selecionada para aprovação. Anexe documentos se necessário.'
      : `${operacoes.length} transferências selecionadas para aprovação. Anexe documentos se necessário.`;

  if (step === 'feedback' && feedbackData) {
    const config = statusConfig[feedbackData.status];
    const itensFalha = feedbackData.itens.filter((i) => !i.sucesso);

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {config.icon}
            <Typography variant="h6" style={{ color: config.color }}>
              {config.titulo}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Box display="flex" style={{ gap: 24, marginBottom: 20 }}>
            {[
              { label: 'Total', value: feedbackData.resumo.totalProcessados },
              { label: 'Sucesso', value: feedbackData.resumo.totalSucesso, color: '#4caf50' },
              { label: 'Falha', value: feedbackData.resumo.totalFalha, color: '#f44336' },
            ].map((card) => (
              <Box
                key={card.label}
                p={1.5}
                style={{ border: '1px solid #e0e0e0', borderRadius: 4, minWidth: 80, textAlign: 'center' }}
              >
                <Typography variant="caption" color="textSecondary">
                  {card.label}
                </Typography>
                <Typography variant="h5" style={{ color: card.color }}>
                  <strong>{card.value}</strong>
                </Typography>
              </Box>
            ))}
          </Box>

          {itensFalha.length > 0 && (
            <>
              <Divider style={{ marginBottom: 16 }} />
              <Typography variant="subtitle1" style={{ marginBottom: 12 }}>
                <strong>{'Falhas na aprovação'}</strong>
              </Typography>
              {itensFalha.map((item, index) => (
                <Box
                  key={index}
                  mb={1}
                  p={1.5}
                  style={{ border: '1px solid #ffcdd2', borderRadius: 4, backgroundColor: '#fff5f5' }}
                >
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {item.codigoIF ?? item.operacaoId}
                  </Typography>
                  <Typography variant="body2" color="error">
                    {item.mensagemErro}
                  </Typography>
                </Box>
              ))}
            </>
          )}

          {documentosFeedback && documentosFeedback.itens.some((i) => !i.sucesso) && (
            <>
              <Box mt={2}>
                <Divider style={{ marginBottom: 16 }} />
              </Box>
              <Typography variant="subtitle1" style={{ marginBottom: 12 }}>
                <strong>{'Falhas no envio de documentos'}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                {'A aprovação foi realizada, porém o documento não foi enviado para os itens abaixo:'}
              </Typography>
              {documentosFeedback.itens
                .filter((i) => !i.sucesso)
                .map((item, index) => {
                  const aprovacaoItem = feedbackData.itens.find((a) => a.operacaoId === item.operacaoId);
                  return (
                    <Box
                      key={index}
                      mb={1}
                      p={1.5}
                      style={{ border: '1px solid #ffe0b2', borderRadius: 4, backgroundColor: '#fff8e1' }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {aprovacaoItem?.codigoIF ?? item.operacaoId}
                      </Typography>
                      <Typography variant="body2" color="error">
                        {item.erro?.mensagem ?? 'Erro desconhecido'}
                      </Typography>
                    </Box>
                  );
                })}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button variant="contained" color="primary" onClick={handleClose}>
            {'Fechar'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5">{'Aprovar Transferências em Lote'}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          {descricaoQuantidade}
        </Typography>
        <SmSpace />

        <Grid container spacing={1} style={{ marginBottom: 8 }}>
          <Grid item xs={2}>
            <Typography variant="caption" color="textSecondary">
              <strong>{'Código'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" color="textSecondary">
              <strong>{'Investidor Origem'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" color="textSecondary">
              <strong>{'Investidor Destino'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              <strong>{'Documento'}</strong>
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {operacoes.map((operacao, index) => (
          <Box key={operacao.id}>
            {index > 0 && <Divider />}
            <Grid container spacing={1} alignItems="center" style={{ padding: '8px 0' }}>
              <Grid item xs={2}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {operacao.instrumentoFinanceiroCodigo}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" noWrap>
                  {operacao.investidorOrigemCpfCnpj ? addCpfCnpjMask(operacao.investidorOrigemCpfCnpj) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" noWrap>
                  {operacao.investidorDestinoCpfCnpj ? addCpfCnpjMask(operacao.investidorDestinoCpfCnpj) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: 'right' }}>
                <CompactUpload
                  arquivos={comprovantesPorOperacao.get(operacao.id) ?? []}
                  onChange={(novos) => handleArquivosChange(operacao.id, novos)}
                  onRemove={(idx) => handleArquivoRemove(operacao.id, idx)}
                  botaoAlinhamento="direita"
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          {'Cancelar'}
        </Button>
        <Button variant="contained" color="primary" onClick={handleAprovar} disabled={submitting}>
          {submitting ? 'Aprovando...' : 'Aprovar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
